import type {
  PapiDiscordSubscriberJoinGuild,
  PapiDiscordOwnerConnectGuild,
  PapiDiscordOwnerConnectionInfo,
  PapiDiscordOwnerDisconnect,
  PapiDiscordOwnerRoles,
} from '@wix/da-papi-types';
import { Action } from 'redux-actions';
import { all, call, put, select, take, takeEvery } from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import { messageReceived } from '../../redux/messages/action';
import { isApiError, requestPuppy, XhrResponse } from '@wix/da-http-client';
import {
  connect,
  connectionSuccess,
  connectionFail,
  disconnect,
  disconnectSuccess,
  disconnectFail,
  fetchConnectionInfo,
  fetchRoles,
  subscriberConnect,
  subscriberConnectSuccess,
  subscriberConnectFail,
  updateConnectionInfo,
  updateRoles,
  reduxInitialized,
} from './actions';
import { getDiscordGuildConnection } from './selectors';
import { showDiscordConnectSuccessModal } from '../../Modals/redux/actionCreators';

const logger = ddt.createLogger('discord');

function fakeConnectionInfo(opts): PapiDiscordOwnerConnectionInfo {
  return {
    connected: Math.random() < 0.5,
    title: 'Example Discord server',
    guildId: 'guild-123',
    oauth2Link: 'https://example.com',
  };
}

function fakeRoles(opts): PapiDiscordOwnerRoles {
  return {
    roles: Array.from({ length: 12 }, (_, idx) => ({
      name: `Role ${idx}`,
      id: `${idx}`,
    })),
  };
}

function* doOauthFlow(oauth2Link: string, messageType: string) {
  const oauthWindow = window.open(oauth2Link);
  const {
    payload: { type, ...message },
  } = yield take(
    (action: Action<any>) =>
      action.type === messageReceived.toString() && action.payload.type === messageType
  );
  oauthWindow?.close();
  return message;
}

function* handleGetConnectionInfo() {
  logger.log('Fetching connection info ...');

  const connectionInfo: XhrResponse<PapiDiscordOwnerConnectionInfo> = yield call(
    requestPuppy,
    {
      method: 'get',
      url: 'owner/connection_info',
    },
    fakeConnectionInfo,
    'discord'
  );

  if (!connectionInfo || isApiError(connectionInfo)) {
    // TODO: deal with it
    logger.log('Failed to get connection info', connectionInfo);
    return;
  }

  yield put(updateConnectionInfo(connectionInfo));
}

function* handleConnect() {
  let connectionInfo: ReturnType<typeof getDiscordGuildConnection> =
    yield select(getDiscordGuildConnection);

  if (!connectionInfo.isReady) {
    yield put(fetchConnectionInfo());
    yield take(updateConnectionInfo);
    connectionInfo = yield select(getDiscordGuildConnection);
  }

  if (connectionInfo.connected) {
    yield put(connectionSuccess(false));
    return;
  }

  if (!connectionInfo.oauth2Link) {
    // TODO: honestly...dunno
    logger.log('Weird connection info', connectionInfo);
    yield put(connectionFail());
    return;
  }

  const message = yield call(doOauthFlow, connectionInfo.oauth2Link, 'OauthDiscordConnect');

  const { access_token: token, guild_id } = message;
  if (!token || !guild_id) {
    // TODO: deal with it
    logger.log('Callback missing required fields', message);
    yield put(connectionFail());
    return;
  }

  const result: XhrResponse<PapiDiscordOwnerConnectGuild> = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'owner/connect_guild',
      data: { token, guild_id },
    },
    undefined,
    'discord'
  );

  if (!result || isApiError(result)) {
    // TODO: deal with it
    logger.log('Failed to connect guild', result);
    yield put(connectionFail());
    return;
  }

  yield put(connectionSuccess(true));
}

function* handleDisconnect() {
  const result: XhrResponse<PapiDiscordOwnerDisconnect> = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'owner/disconnect',
    },
    undefined,
    'discord'
  );

  if (!result || isApiError(result)) {
    // TODO: deal with it
    logger.log('Failed to disconnect guild', result);
    yield put(disconnectFail());
    return;
  }

  yield put(disconnectSuccess());
}

function* handleSubscriberConnect({
  payload: { oauth2Url },
}: ReturnType<typeof subscriberConnect>) {
  if (!oauth2Url) {
    // TODO: deal with it
    logger.log('Missing oauth2 link');
    yield put(subscriberConnectFail());
    return;
  }
  const message = yield call(doOauthFlow, oauth2Url, 'OauthDiscordSubscribe');

  const { access_token: token, state: tier_id } = message;
  if (!token || !tier_id) {
    // TODO: deal with it
    logger.log('Callback missing required fields', message);
    yield put(subscriberConnectFail());
    return;
  }

  const result: XhrResponse<PapiDiscordSubscriberJoinGuild> = yield call(
    requestPuppy,
    {
      method: 'post',
      url: 'subscriber/join_guild',
      data: { token, tier_id },
    },
    undefined,
    'discord'
  );

  if (!result || isApiError(result)) {
    // TODO: deal with it
    logger.log('Failed to add member to guild', result);
    yield put(subscriberConnectFail());
    return;
  }

  yield put(subscriberConnectSuccess(result.guildId));
}

function* handleFetchRoles() {
  const result: XhrResponse<PapiDiscordOwnerRoles> = yield call(
    requestPuppy,
    {
      method: 'get',
      url: 'owner/roles',
    },
    fakeRoles,
    'discord'
  );

  if (!result || isApiError(result)) {
    // TODO: deal with it
    logger.log('Failed to get roles', result);
    return;
  }

  yield put(updateRoles(result.roles));
}

function* handleSubscriberConnectSuccess(action: ReturnType<typeof subscriberConnectSuccess>) {
  yield put(showDiscordConnectSuccessModal(action.payload.guildId));
}

export default function* saga() {
  yield take(reduxInitialized); // wait for redux to load

  yield all([
    takeEvery(connect, handleConnect),
    takeEvery(disconnect, handleDisconnect),
    takeEvery(subscriberConnect, handleSubscriberConnect),
    takeEvery(fetchConnectionInfo, handleGetConnectionInfo),
    takeEvery(fetchRoles, handleFetchRoles),
    takeEvery(subscriberConnectSuccess, handleSubscriberConnectSuccess),
  ]);
}
